import { Loader } from "@googlemaps/js-api-loader";
import analytics from "../../analytics/analytics.js";
import { GOOGLE_MAP_API_KEY, PRICELINE_MAP_ID } from "../../constants/googleMapConst.js";
import { createMapAdvancedMarkerElement } from "./createMapAdvancedMarkerElement.js";
const initMapManager = ()=>{
    let map = null;
    let loader = null;
    let mapMarkerLib = null;
    let mapCoreLib = null;
    let mapContainer = null;
    let seti;
    const callbacks = [];
    const markers = new Map();
    const resolveMarkerKey = (pin)=>{
        if (!pin) return null;
        if (pin?.hotelId) return pin.hotelId;
        return pin?.name ?? null;
    };
    const removeMarkerByKey = (key)=>{
        if (!key || !markers.has(key)) return;
        const entry = markers.get(key);
        const marker = entry?.marker;
        const root = entry?.root;
        if (marker) marker.map = null;
        if (root) root.unmount();
        markers.delete(key);
    };
    const clearMarkersInternal = ()=>{
        Array.from(markers.keys()).forEach((key)=>removeMarkerByKey(key));
    };
    const ensureContainer = ()=>{
        const container = document.getElementById('PENNY-SEARCH-GOOGLE-MAP');
        if (!container) {
            analytics.logError({
                loggedFrom: 'initMapManager:ensureContainer',
                message: 'Missing PENNY-SEARCH-GOOGLE-MAP container'
            });
            return null;
        }
        if (mapContainer && mapContainer !== container) {
            clearMarkersInternal();
            map = null;
            mapMarkerLib = null;
            mapCoreLib = null;
        }
        mapContainer = container;
        return container;
    };
    return {
        initloader: (options)=>{
            if ('undefined' != typeof window && 'function' == typeof window.google?.maps?.importLibrary) loader = window.google.maps;
            if (!loader) loader = new Loader({
                apiKey: GOOGLE_MAP_API_KEY,
                version: 'weekly'
            });
            if (options?.setiValues) seti = {
                ...options.setiValues
            };
            return loader;
        },
        setMap: async ()=>{
            const container = ensureContainer();
            if (!container) return map;
            if (map && mapMarkerLib && mapCoreLib) return map;
            const { Map: Map1 } = await loader.importLibrary('maps');
            map = new Map1(container, {
                zoomControl: true,
                fullscreenControl: false,
                cameraControl: false,
                streetViewControl: false,
                mapTypeControl: false,
                center: {
                    lat: 40.7128,
                    lng: -74.006
                },
                zoom: 14,
                minZoom: 2,
                maxZoom: 20,
                mapId: PRICELINE_MAP_ID
            });
            mapMarkerLib = await loader.importLibrary('marker');
            mapCoreLib = await loader.importLibrary('core');
            map.addListener('zoom_changed', ()=>{
                const currentZoom = map.getZoom();
                const currentCenter = map.getCenter();
                if (2 === currentZoom && currentCenter) map.setCenter({
                    lat: currentCenter.lat() + 0.2,
                    lng: currentCenter.lng()
                });
            });
            return map;
        },
        addCallback: function(cb) {
            callbacks.push(cb);
            if (map) this.fireCallbacks();
        },
        fireCallbacks: function() {
            while(callbacks.length > 0){
                const cb = callbacks.shift();
                const options = {
                    map,
                    loader
                };
                cb(options);
            }
        },
        fitBoundsOnCurrentMap: async function(listOfPins) {
            if (!map || !loader || !Array.isArray(listOfPins) || 0 === listOfPins.length) return;
            try {
                const { LatLngBounds } = mapCoreLib;
                const bounds = new LatLngBounds();
                listOfPins.forEach((pin)=>{
                    const latLng = {
                        lat: Number(pin.lat),
                        lng: Number(pin.lng)
                    };
                    if (!Number.isNaN(latLng.lat) && !Number.isNaN(latLng.lng)) bounds.extend(latLng);
                });
                if (1 === listOfPins.length) {
                    map.setCenter(bounds.getCenter());
                    map.setZoom(17);
                } else map.fitBounds(bounds);
            } catch (e) {
                analytics.logError({
                    loggedFrom: 'initMapManager:fitBoundsOnCurrentMap',
                    message: 'Failed to call fitBounds on map',
                    error: e.message
                });
            }
        },
        createAdvancedMarker: function(options, markerKey) {
            const key = markerKey ?? resolveMarkerKey(options);
            if (!key) return null;
            if (!mapMarkerLib) return null;
            const { AdvancedMarkerElement } = mapMarkerLib;
            const existingEntry = markers.get(key);
            if (existingEntry) {
                const { pinDetails } = existingEntry;
                const latUnchanged = Number(pinDetails.lat) === Number(options.lat);
                const lngUnchanged = Number(pinDetails.lng) === Number(options.lng);
                if (latUnchanged && lngUnchanged) return existingEntry.marker;
                removeMarkerByKey(key);
            }
            const markerResult = createMapAdvancedMarkerElement({
                gMap: map,
                AdvancedMarkerElement,
                pinDetails: options,
                seti
            });
            if (markerResult) {
                const { marker, root } = markerResult;
                markers.set(key, {
                    marker,
                    root,
                    pinDetails: {
                        ...options
                    }
                });
                return marker;
            }
            return null;
        },
        syncMarkers: function(pinMap) {
            if (!map) return;
            const desiredKeys = new Set(Object.keys(pinMap ?? {}));
            Array.from(markers.keys()).forEach((key)=>{
                if (!desiredKeys.has(key)) removeMarkerByKey(key);
            });
            desiredKeys.forEach((key)=>{
                const pin = pinMap[key];
                if (pin) this.createAdvancedMarker(pin, key);
            });
        },
        clearMarkers: function() {
            clearMarkersInternal();
        }
    };
};
const mapManager = initMapManager();
export { initMapManager, mapManager };
